import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Card,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { 
  Download as DownloadIcon, 
  Add as AddIcon, 
  CloudUpload as CloudUploadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CalendarToday as CalendarTodayIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import API_BASE_URL from '../config/api';

const Payslips = () => {
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [sendingEmail, setSendingEmail] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [progressMessages, setProgressMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState('');
  const [sendEmail, setSendEmail] = useState(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  useEffect(() => {
    fetchPayslips();
  }, [page, rowsPerPage, search, periodFilter, startDate, endDate]);

  const fetchPayslips = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      
      if (search) params.search = search;
      if (periodFilter && !startDate && !endDate) params.periodFilter = periodFilter;
      if (startDate) params.startDate = startDate.toISOString().split('T')[0];
      if (endDate) params.endDate = endDate.toISOString().split('T')[0];

      const response = await axios.get(`${API_BASE_URL}/api/payslips`, { params });
      setPayslips(response.data.data);
      setTotal(response.data.pagination?.total || 0);
      setError('');
    } catch (err) {
      setError('Failed to fetch payslips');
      console.error('Error fetching payslips:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (payslipId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payslips/download/${payslipId}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${payslipId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to download payslip');
      console.error('Error downloading payslip:', err);
    }
  };

  const handleSendEmail = async (payslipId) => {
    setSendingEmail(prev => ({ ...prev, [payslipId]: true }));
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payslips/send-email/${payslipId}`);
      if (response.data.success) {
        alert(`Payslip sent successfully to ${response.data.data.email}`);
      } else {
        alert(response.data.message || 'Failed to send email');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to send email';
      alert(errorMsg);
      console.error('Error sending email:', err);
    } finally {
      setSendingEmail(prev => ({ ...prev, [payslipId]: false }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setError('');
      } else {
        alert('Please select a CSV file');
        setSelectedFile(null);
      }
    }
  };

  const handleGeneratePayslips = async () => {
    if (!selectedFile) {
      alert('Please select a CSV file');
      return;
    }
    if (!selectedMonth) {
      alert('Please select a month');
      return;
    }
    if (!selectedYear) {
      alert('Please select a year');
      return;
    }

    setGenerating(true);
    setUploadProgress(0);
    setError('');
    setProgressMessages([]);
    setCurrentStep('');

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('month', selectedMonth);
    formData.append('year', selectedYear.toString());
    formData.append('sendEmail', sendEmail.toString());

    try {
      const response = await fetch(`${API_BASE_URL}/api/payslips/upload-and-generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to process CSV');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.message) {
                setProgressMessages(prev => [...prev, {
                  message: data.message,
                  timestamp: new Date(),
                  employeeNo: data.employeeNo,
                  employeeName: data.employeeName,
                  success: data.success,
                  error: data.error,
                  progress: data.progress,
                  current: data.current,
                  total: data.total
                }]);
              }

              if (data.progress !== undefined) {
                setUploadProgress(data.progress);
              }

              if (data.step) {
                setCurrentStep(data.step);
              }

              if (data.step === 'complete' || data.message === 'complete') {
                setGenerating(false);
                setTimeout(() => {
                  alert(`Successfully processed ${data.stats?.total || 0} employees. Generated ${data.stats?.successful || 0} payslips!`);
                  setGenerateDialogOpen(false);
                  setSelectedFile(null);
                  setSelectedMonth('');
                  setSelectedYear(new Date().getFullYear());
                  setSendEmail(false);
                  document.getElementById('file-input').value = '';
                  setProgressMessages([]);
                  fetchPayslips();
                }, 1000);
                return;
              }

              if (data.error || data.message === 'error') {
                setError(data.error || 'An error occurred');
                setGenerating(false);
                return;
              }
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to generate payslips';
      setError(errorMsg);
      setGenerating(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handlePeriodFilterChange = (e) => {
    setPeriodFilter(e.target.value);
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const handleClearFilters = () => {
    setSearch('');
    setPeriodFilter('');
    setStartDate(null);
    setEndDate(null);
    setPage(0);
  };

  const hasActiveFilters = search || periodFilter || startDate || endDate;

  if (loading && payslips.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ 
        mb: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: '#1e293b' }}>
            Payslips
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage and download employee payslips
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setGenerateDialogOpen(true)}
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 1,
            textTransform: 'none',
            fontSize: '0.95rem',
            fontWeight: 600,
            boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.39)',
          }}
        >
          Generate Payslips
        </Button>
      </Box>

      {/* Filters Section */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: '#6366f1' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          {hasActiveFilters && (
            <Tooltip title="Clear all filters">
              <IconButton 
                size="small" 
                onClick={handleClearFilters}
                sx={{ ml: 'auto' }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search by Name or Employee ID"
              variant="outlined"
              value={search}
              onChange={handleSearchChange}
              placeholder="Enter name or employee ID..."
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: '#94a3b8' }} />,
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Quick Period Filter</InputLabel>
              <Select
                value={periodFilter}
                onChange={handlePeriodFilterChange}
                label="Quick Period Filter"
                disabled={!!(startDate || endDate)}
              >
                <MenuItem value="">All Periods</MenuItem>
                <MenuItem value="current_year">Current Year</MenuItem>
                <MenuItem value="previous_year">Previous Year</MenuItem>
                <MenuItem value="last_month">Last Month</MenuItem>
                <MenuItem value="last_3_months">Last 3 Months</MenuItem>
                <MenuItem value="last_6_months">Last 6 Months</MenuItem>
                <MenuItem value="last_9_months">Last 9 Months</MenuItem>
                <MenuItem value="last_12_months">Last 12 Months</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate ? startDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                setStartDate(e.target.value ? new Date(e.target.value) : null);
                setPeriodFilter('');
              }}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={2.5}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate ? endDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                setEndDate(e.target.value ? new Date(e.target.value) : null);
                setPeriodFilter('');
              }}
              inputProps={{
                min: startDate ? startDate.toISOString().split('T')[0] : undefined,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              InputProps={{
                startAdornment: <CalendarTodayIcon sx={{ mr: 1, color: '#94a3b8', fontSize: 20 }} />,
              }}
            />
          </Grid>
        </Grid>

        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {search && (
              <Chip 
                label={`Search: ${search}`} 
                onDelete={() => setSearch('')}
                color="primary"
                variant="outlined"
              />
            )}
            {periodFilter && (
              <Chip 
                label={`Period: ${periodFilter.replace('_', ' ')}`} 
                onDelete={() => setPeriodFilter('')}
                color="primary"
                variant="outlined"
              />
            )}
            {startDate && (
              <Chip 
                label={`From: ${startDate.toLocaleDateString()}`} 
                onDelete={() => setStartDate(null)}
                color="primary"
                variant="outlined"
              />
            )}
            {endDate && (
              <Chip 
                label={`To: ${endDate.toLocaleDateString()}`} 
                onDelete={() => setEndDate(null)}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table Section */}
      <Card sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Employee No</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Employee Name</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Period</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Generated At</TableCell>
                <TableCell sx={{ fontWeight: 600, color: '#475569' }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payslips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No payslips found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                payslips.map((payslip) => (
                  <TableRow 
                    key={payslip.id}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f8fafc',
                        cursor: 'pointer'
                      },
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={payslip.employee_no} 
                        size="small"
                        sx={{ 
                          backgroundColor: '#ede9fe',
                          color: '#6366f1',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{payslip.employee_name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={payslip.period} 
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: '#cbd5e1' }}
                      />
                    </TableCell>
                    <TableCell color="text.secondary">
                      {new Date(payslip.generated_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownload(payslip.id)}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            px: 2,
                            py: 0.5,
                            fontSize: '0.875rem',
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            },
                          }}
                        >
                          Download
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={sendingEmail[payslip.id] ? <CircularProgress size={16} /> : <EmailIcon />}
                          onClick={() => handleSendEmail(payslip.id)}
                          disabled={sendingEmail[payslip.id]}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 1,
                            px: 2,
                            py: 0.5,
                            fontSize: '0.875rem',
                            borderColor: '#6366f1',
                            color: '#6366f1',
                            '&:hover': {
                              borderColor: '#4f46e5',
                              backgroundColor: 'rgba(99, 102, 241, 0.04)',
                            },
                            '&:disabled': {
                              borderColor: '#cbd5e1',
                              color: '#94a3b8',
                            },
                          }}
                        >
                          {sendingEmail[payslip.id] ? 'Sending...' : 'Send Email'}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Card>

      {/* Generate Payslips Dialog */}
      <Dialog 
        open={generateDialogOpen} 
        onClose={() => {
          if (!generating) {
            setGenerateDialogOpen(false);
            setSelectedFile(null);
            setSelectedMonth('');
            setSelectedYear(new Date().getFullYear());
            setSendEmail(false);
            setProgressMessages([]);
            setUploadProgress(0);
            setCurrentStep('');
            document.getElementById('file-input').value = '';
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: 'white',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          '& .MuiDialogTitle-root': {
            borderRadius: 0,
          }
        }}>
          Generate Payslips from CSV
          {!generating && (
            <IconButton
              onClick={() => {
                setGenerateDialogOpen(false);
                setSelectedFile(null);
                setSelectedMonth('');
                setSelectedYear(new Date().getFullYear());
                setSendEmail(false);
                setProgressMessages([]);
                setUploadProgress(0);
                setCurrentStep('');
                document.getElementById('file-input').value = '';
              }}
              sx={{ color: 'white' }}
              size="small"
            >
              <ClearIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="file-input"
                type="file"
                onChange={handleFileChange}
                disabled={generating}
              />
              <label htmlFor="file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={generating}
                  fullWidth
                  sx={{
                    py: 2,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    borderRadius: 1,
                    textTransform: 'none',
                  }}
                >
                  {selectedFile ? selectedFile.name : 'Select CSV File'}
                </Button>
              </label>
              {selectedFile && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </Typography>
              )}
            </Box>

            <FormControl fullWidth>
              <InputLabel>Select Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                label="Select Month"
                disabled={generating}
              >
                {months.map((month) => (
                  <MenuItem key={month} value={month}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Select Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                label="Select Year"
                disabled={generating}
              >
                {years.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  disabled={generating}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  Send payslips via email to employees
                </Typography>
              }
              sx={{ mt: -1 }}
            />

            {generating && (
              <Box>
                <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 1, height: 8 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                  {currentStep === 'upload' ? 'Uploading CSV...' : 
                   currentStep === 'parse' ? 'Parsing CSV...' : 
                   currentStep === 'generate' ? 'Generating payslips...' : 
                   currentStep === 'cleanup' ? 'Finalizing...' : 
                   'Processing...'} {uploadProgress}%
                </Typography>
                
                {progressMessages.length > 0 && (
                  <Box sx={{ 
                    maxHeight: 200, 
                    overflowY: 'auto', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: 1, 
                    p: 2,
                    backgroundColor: '#f8fafc'
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
                      Progress Log:
                    </Typography>
                    {progressMessages.slice(-10).map((msg, idx) => (
                      <Typography 
                        key={idx} 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.75rem',
                          color: msg.error ? 'error.main' : msg.success ? 'success.main' : 'text.secondary',
                          mb: 0.5,
                          fontFamily: 'monospace'
                        }}
                      >
                        {msg.message}
                        {msg.current && msg.total && (
                          <span style={{ color: '#64748b', marginLeft: '8px' }}>
                            ({msg.current}/{msg.total})
                          </span>
                        )}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 1 }}>
              Upload a CSV file with all employee payroll data for the selected month and year.
              The system will process the CSV, update/create employee and payroll records, and generate payslips automatically.
              {sendEmail && ' Payslips will be sent to employees via email.'}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => {
              if (!generating) {
                setGenerateDialogOpen(false);
                setSelectedFile(null);
                setSelectedMonth('');
                setSelectedYear(new Date().getFullYear());
                setSendEmail(false);
                setProgressMessages([]);
                setUploadProgress(0);
                setCurrentStep('');
                document.getElementById('file-input').value = '';
              }
            }} 
            disabled={generating}
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGeneratePayslips}
            variant="contained"
            disabled={generating || !selectedFile || !selectedMonth || !selectedYear}
            sx={{ textTransform: 'none', borderRadius: 1, minWidth: 150 }}
          >
            {generating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                <span>Generating...</span>
              </Box>
            ) : (
              'Generate Payslips'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payslips;
